
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Tables } from '@/integrations/supabase/types';

type UserSubscription = Tables<'user_subscriptions'>;

export const useSubscription = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  const { data: subscriptionData, refetch, isLoading: queryLoading } = useQuery({
    queryKey: ['subscription', user?.id],
    queryFn: async () => {
      if (!user?.id) return { subscription: null, profile: null, isAdminChild: false };
      
      console.log('🔍 [useSubscription] Checking subscription for user:', user.id);
      
      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('parent_id, role, full_name')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('❌ [useSubscription] Error fetching profile:', profileError);
        return { subscription: null, profile: null, isAdminChild: false };
      }

      console.log('👤 [useSubscription] User profile:', profile);

      // Check if this is a child of an admin parent using RPC to bypass RLS
      let isAdminChild = false;
      if (profile?.role === 'child') {
        const { data: isParentAdmin, error: parentCheckError } = await supabase
          .rpc('is_parent_admin', { child_user_id: user.id });

        if (parentCheckError) {
          console.error('❌ [useSubscription] Error checking parent admin status via RPC:', parentCheckError);
        } else if (isParentAdmin === true) {
          isAdminChild = true;
          console.log('🔓 [useSubscription] Admin parent detected via RPC - granting free access');
        }
      }

      // Check for user's own active subscription that hasn't expired
      const { data: subscription, error: subscriptionError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      if (subscriptionError) {
        console.error('❌ [useSubscription] Error fetching subscription:', subscriptionError);
      }

      console.log('💳 [useSubscription] User subscription:', subscription);

      return { 
        subscription, 
        profile,
        isAdminChild
      };
    },
    enabled: !!user?.id,
  });

  const createCheckoutSession = async (subscriptionType: 'monthly' | 'quarterly' | 'annual') => {
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: {
        subscription_type: subscriptionType,
      },
    });

    if (error) throw error;
    return data;
  };

  const verifyPayment = async (sessionId: string) => {
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase.functions.invoke('verify-payment', {
      body: { session_id: sessionId },
    });

    if (error) throw error;
    
    // Refetch subscription data after successful payment
    if (data.success) {
      await refetch();
    }
    
    return data;
  };

  const subscription = subscriptionData?.subscription;
  const isAdminChild = subscriptionData?.isAdminChild || false;
  
  // Check if subscription is active and not expired
  const hasActiveSubscription = (!!subscription && subscription.is_active && (!subscription.expires_at || new Date(subscription.expires_at) > new Date())) || isAdminChild;
  const subscriptionType = subscription?.subscription_type;
  const durationMonths = subscription?.duration_months;
  const expiresAt = subscription?.expires_at;
  const isInherited = isAdminChild; // Admin children inherit access from parent

  useEffect(() => {
    setIsLoading(queryLoading);
  }, [queryLoading]);

  return {
    subscription,
    hasActiveSubscription,
    subscriptionType,
    durationMonths,
    expiresAt,
    isLoading,
    isInherited,
    createCheckoutSession,
    verifyPayment,
    refetch: async () => {
      const result = await refetch();
      return {
        hasActiveSubscription: result.data?.subscription || result.data?.isAdminChild || false,
        subscription: result.data?.subscription || null
      };
    },
  };
};
